import { useCallback, useState } from 'react';
import { useUnpublishWorkoutByIdMutation } from 'generated/graphql';
import { makeAsync } from '@client/new/helpers/async';
import { Async } from '@client/new/helpers/types';
import { postScheduleGroup, toErrorMessage } from './scheduleGroup.api';
import { ScheduleGroupRequest } from './scheduleGroups.domain';

export const useScheduleGroupActions = (
  programId: number,
  onChanged: () => void,
) => {
  const [state, setState] = useState<Async<null>>(makeAsync.notFetched);
  const [unpublishWorkout] = useUnpublishWorkoutByIdMutation();

  const run = useCallback(
    (task: () => Promise<unknown>) => {
      setState(makeAsync.fetching());
      return task()
        .then(() => {
          setState(makeAsync.fetched(null));
          return true;
        })
        .catch((error: unknown) => {
          setState(makeAsync.error(toErrorMessage(error)));
          return false;
        })
        .finally(onChanged);
    },
    [onChanged],
  );

  const scheduleGroup = useCallback(
    (request: ScheduleGroupRequest) =>
      run(() => postScheduleGroup(programId, request)),
    [run, programId],
  );

  const unschedule = useCallback(
    (workoutIds: Array<number>) =>
      run(() =>
        Promise.all(
          workoutIds.map((id) => unpublishWorkout({ variables: { id } })),
        ),
      ),
    [run, unpublishWorkout],
  );

  return { state, scheduleGroup, unschedule };
};
